import React, { useEffect, useState } from "react";
import { Plus } from "react-bootstrap-icons";
import { Edit2, Trash2 } from "lucide-react";
import ModalWrapper from "../../../../components/learning-development/common/ModalWrapper";

import DynamicTable from "../../../../components/learning-development/common/DynamicTable";
import api from "../../../../Axios";
import { toast } from "react-toastify";
import { TextField } from "@mui/material";
import ExpandableText from "../../../../components/common/ExpandableText";
import { defaultDateFormatter } from "../../../../Helper/functionHelper";
import CommonFilter from "../../../../components/common/CommonFilter";
import useLoader from "../../../../hooks/useLoader";

const filterConfig = [
  {
    name: "search",
    type: "text",
    label: "Search by category name",
    grid: 3,
  },
  {
    name: "start_date",
    type: "date",
    label: "From Date",
    grid: 3,
  },
  {
    name: "end_date",
    type: "date",
    label: "To Date",
    grid: 3,
  },
];

const ProgramCategoryList = () => {

  const { hideLoader, showLoader } = useLoader();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({
    search: "",
    start_date: null,
    end_date: null,
  });

  const [modalType, setModalType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [categoryForm, setCategoryForm] = useState({
    title: "",
    description: ""
  });
  const [errors, setErrors] = useState({});

  const fetchCategories = async (page = currentPage, customFilters = filters) => {
    try {
      showLoader();

      const res = await api.post("/lnd/get-category-list", {
        page,
        limit: itemsPerPage,
        searchData: {
          ...customFilters,
          search: customFilters.search?.trim() || ""
        }
      });

      const pagination = res.data.data.pagination;

      setCategories(res.data.data.data || []);
      setTotalPages(pagination.totalPages);
      setTotalItems(pagination.total);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Server error");
    } finally {
      hideLoader();
    }
  };

  const handleExport = async (filters) => {
    const res = await api.post(
      "/lnd/export-category-list",
      { searchData: filters },
      { responseType: "blob" }
    );

    const blob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "category_export.xlsx";
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchCategories(currentPage, filters);
  }, [currentPage, itemsPerPage]);

  const validateForm = () => {
    let newErrors = {};
    const title = categoryForm.title?.trim();
    const validPattern = /^[A-Za-z0-9 _&-]+$/;

    if (!title) {
      newErrors.title = "Category name is required";
    }
    else if (title.length > 50) {
      newErrors.title = "Category name max 50 characters";
    }
    else if (!validPattern.test(title)) {
      newErrors.title =
        "Only letters, numbers, hyphen (-), ampersand (&), and underscore (_) are allowed";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateCategory = async () => {
    setLoading(true);
    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      await api.post("/lnd/add-categories", {
        title: categoryForm.title?.trim(),
        description: categoryForm.description?.trim(),
      });
      toast.success("Category created");
      closeModal();
      fetchCategories(currentPage, filters);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create category");
    }
    finally {
      setLoading(false);
    }
  };

  const handleUpdateCategory = async () => {
    setLoading(true);
    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      await api.put(`/lnd/update-category/${selectedCategory.id}`, {
        title: categoryForm.title?.trim(),
        description: categoryForm.description?.trim(),
      });
      toast.success("Category updated");
      closeModal();
      fetchCategories(currentPage, filters);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update category");
    }
    finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    setLoading(true);
    try {
      await api.delete(`/lnd/delete-category/${selectedCategory.id}`);
      toast.success("Category deleted");
      closeModal();
      fetchCategories(currentPage, filters);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete category");
    }
    finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setCategoryForm({ title: "", description: "" });
    setErrors({});
    setModalType("create");
  };

  const openEditModal = (row) => {
    setSelectedCategory(row);
    setCategoryForm({
      title: row.title,
      description: row.description || ""
    });
    setErrors({});
    setModalType("edit");
  };

  const openDeleteModal = (row) => {
    setSelectedCategory(row);
    setModalType("delete");
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedCategory(null);
    setErrors({});
  };

  const columns = [
    {
      label: "Category Name",
      field: "title",
      className: "fw-semibold"
    },
    {
      label: "No of Programs",
      render: (row) => (
        <span>{row.programCount || 0}</span>
      )
    },
    {
      label: "Description",
      render: (row) => (
        <ExpandableText text={row.description} label="Category Description" />
      )
    },
    {
      label: "Created At",
      render: (row) => defaultDateFormatter(row.createdAt)
    },
    {
      label: "Actions",
      render: (row) => (
        <div className="d-flex gap-2">
          <div
            className="cursor-pointer"
            onClick={() => openEditModal(row)}
          >
            <Edit2 size={16} />
          </div>

          {Number(row.programCount) === 0 && <div
            className="cursor-pointer"
            onClick={() => openDeleteModal(row)}
          >
            <Trash2 size={16} />
          </div>}
        </div>
      )
    }
  ];

  return (
    <div className="container-fluid py-4">

      <div className="row g-2 align-items-end mb-4">

        <div className="col-12 col-lg">
          <CommonFilter
            config={filterConfig}
            defaultValues={{
              search: "",
              start_date: null,
              end_date: null,
            }}
            onSubmit={(data) => {
              setFilters(data);
              setCurrentPage(1);
              fetchCategories(1, data);
            }}
            onExport={handleExport}
          />
        </div>

        <div className="col-12 col-lg-auto d-flex justify-content-end">
          <button
            onClick={openCreateModal}
            className="btn btn-success d-flex align-items-center justify-content-center"
            style={{ width: "40px", height: "40px" }}
          >
            <Plus size={24} />
          </button>
        </div>

      </div>

      <DynamicTable
        columns={columns}
        data={categories}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={(value) => setItemsPerPage(value)}
        totalItems={totalItems}
        onPageChange={(page) => setCurrentPage(page)}
        rowKey="id"
        loading={loading}
      />

      {(modalType === "create" || modalType === "edit") && (
        <ModalWrapper onClose={closeModal}>
          <form
            onSubmit={(e) => {
              e.preventDefault();

              if (modalType === "create") {
                handleCreateCategory();
              } else {
                handleUpdateCategory();
              }
            }}
          >
            <h5 className="fw-bold mb-4 text-dark">
              {modalType === "create" ? "Create Category" : "Edit Category"}
            </h5>

            <TextField
              fullWidth
              label="Category Name *"
              variant="outlined"
              size="small"
              value={categoryForm.title}
              onChange={(e) => {
                setCategoryForm({ ...categoryForm, title: e.target.value });
                if (errors.title) setErrors({ ...errors, title: "" });
                if ((e.target.value.trim()).length > 50) setErrors({ ...errors, title: "Category name max 50 characters" });
              }}
              error={!!errors.title}
              helperText={errors.title}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Description"
              variant="outlined"
              size="small"
              multiline
              rows={3}
              value={categoryForm.description}
              onChange={(e) =>
                setCategoryForm({
                  ...categoryForm,
                  description: e.target.value
                })
              }
              sx={{ mb: 4 }}
            />

            <div className="d-flex justify-content-end gap-3">
              <button
                type="button" // ✅ important
                className="btn btn-secondary px-4"
                onClick={closeModal}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn btn-success px-4"
                disabled={loading}
              >
                {modalType === "create" ? "Create" : "Update"}
              </button>
            </div>
          </form>
        </ModalWrapper>
      )}

      {/* DELETE MODAL */}
      {modalType === "delete" && (
        <ModalWrapper onClose={closeModal}>
          <div className="text-center p-2">
            <h5 className="fw-bold mb-3 text-dark">Delete Category</h5>
            <p className="text-muted mb-4">
              Are you sure you want to delete{" "}
              <strong className="text-dark">{selectedCategory?.title}</strong>{" "}?
              <br />
              This action cannot be undone.
            </p>

            <div className="d-flex justify-content-center gap-3">
              <button
                className="btn btn-secondary px-4"
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger px-4"
                disabled={loading}
                onClick={handleDeleteCategory}
              >
                Delete
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};

export default ProgramCategoryList;